import Select, { type StylesConfig } from 'react-select';
import { useVelzonSelectStyles, type VpSelectOption } from './velzonSelectStyles';

export interface VpSelectProps {
  options: VpSelectOption[];
  /** Single-select value (matches the native <select> value contract). */
  value: string | number | null | undefined;
  onChange: (value: string | number | null) => void;
  ariaLabel?: string;
  inputId?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  /** Optional fixed width to mimic the previous inline select sizing. */
  width?: number | string;
}

/**
 * Single-select wrapper around react-select with the shared Velzon styling
 * (SP-5h). Exposes a native-<select>-like value/onChange API so call sites and
 * tests stay simple. `classNamePrefix="react-select"` matches the reference
 * Velzon markup. For tests use `react-select-event`:
 * `selectEvent.select(getByLabelText(ariaLabel), 'Label')`.
 */
export function VpSelect({
  options, value, onChange, ariaLabel, inputId, placeholder,
  isDisabled, isClearable, width,
}: VpSelectProps) {
  const selected = options.find((o) => o.value === value) ?? null;
  const baseStyles = useVelzonSelectStyles();
  const styles: StylesConfig<VpSelectOption, false> = {
    ...(baseStyles as StylesConfig<VpSelectOption, false>),
    menuPortal: (base) => ({ ...base, zIndex: 1080 }),
    ...(width !== undefined ? { container: (base) => ({ ...base, width }) } : {}),
  };
  return (
    <Select<VpSelectOption>
      options={options}
      value={selected}
      onChange={(opt) => onChange(opt ? opt.value : null)}
      styles={styles}
      classNamePrefix="react-select"
      aria-label={ariaLabel}
      inputId={inputId}
      placeholder={placeholder ?? 'Seleziona…'}
      isDisabled={isDisabled}
      isClearable={isClearable}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
    />
  );
}
